import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { api, euro, BACKEND_URL, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AdminCost } from "@/components/AdminSourceCost";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PhoneDisplay } from "@/components/PhoneDisplay";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Truck, Check, Copy, ExternalLink, ArrowLeft, BookUser, AlertTriangle, Loader2, Eye, EyeOff, Search, Store, UserRound } from "lucide-react";
import { ServiceHistoryButton } from "@/components/ServiceHistoryButton";
import { LockerPicker } from "@/components/LockerPicker";
import { ShipmentPickupButton } from "@/components/ShipmentPickup";
import { StickyActions } from "@/components/StickyActions";
import DealerShippingWizard from "@/pages/DealerShippingWizard";
import { contactTitle } from "@/components/ContactDialog";

const emptyAddr = (country = "IT") => ({ name: "", address: "", city: "", zip: "", country, phone: "", email: "" });

const COUNTRIES = [
  ["IT", "Italia"], ["SM", "San Marino"], ["VA", "Città del Vaticano"], ["FR", "Francia"], ["DE", "Germania"],
  ["ES", "Spagna"], ["CH", "Svizzera"], ["AT", "Austria"], ["BE", "Belgio"], ["NL", "Paesi Bassi"],
  ["PT", "Portogallo"], ["GB", "Regno Unito"], ["IE", "Irlanda"], ["GR", "Grecia"], ["PL", "Polonia"],
  ["SE", "Svezia"], ["DK", "Danimarca"], ["US", "Stati Uniti"],
];
const CountrySelect = ({ value, onChange, testid }) => (
  <Select value={value || "IT"} onValueChange={onChange}>
    <SelectTrigger className="mt-1" data-testid={testid}><SelectValue placeholder="Paese" /></SelectTrigger>
    <SelectContent className="max-h-64">{COUNTRIES.map(([c, n]) => <SelectItem key={c} value={c}>{n}</SelectItem>)}</SelectContent>
  </Select>
);

const norm = (s) => (s || "").trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

const CARRIER_COLORS = ["bg-blue-600", "bg-teal-600", "bg-amber-500", "bg-purple-600", "bg-rose-600", "bg-emerald-600"];
const monoColor = (s) => CARRIER_COLORS[[...(s || "?")].reduce((a, c) => a + c.charCodeAt(0), 0) % CARRIER_COLORS.length];
const initials = (s) => (s || "?").split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase();
const CarrierLogo = ({ c }) => (c.logo
  ? <img src={c.logo} alt={c.name} className="w-10 h-10 rounded-md object-contain bg-white p-1 shrink-0" />
  : <span className={`w-10 h-10 rounded-md ${monoColor(c.name)} text-white flex items-center justify-center font-bold text-sm shrink-0`}>{initials(c.name)}</span>);

const STEPS = [{ n: 1, t: "Dati & indirizzi" }, { n: 2, t: "Corriere" }, { n: 3, t: "Contenuto & pagamento" }];

// Campi minimi obbligatori per mittente/destinatario prima di poter procedere.
const REQ_FIELDS = ["name", "address", "city", "zip", "phone"];
const FIELD_LABEL = { name: "nome", address: "indirizzo e civico", city: "città", zip: "CAP", phone: "telefono", country: "paese" };
const missingOf = (a) => REQ_FIELDS.filter((k) => !((a?.[k] || "").trim()));

const ShopSummary = ({ who, s, testid }) => {
  const a = s?.address || {};
  const line = [a.name, a.address, [a.zip, a.city].filter(Boolean).join(" "), a.country].filter(Boolean).join(" · ");
  return (
    <div className="rounded-xl border border-border p-4 text-sm" data-testid={testid}>
      <p className="text-muted-foreground">{who}: <strong className="text-foreground">il tuo negozio</strong> (dalla tua vetrina)</p>
      <p className="mt-1">{line || "—"}</p>
      <Link to="/app/vetrina" className="text-primary underline text-xs mt-1 inline-block">Modifica in "La mia vetrina"</Link>
    </div>
  );
};

const AddrForm = ({ title, v, on, book, onLoad, save, onSave, testid, onCapStatus }) => {
  const [geo, setGeo] = useState(null);
  const [checking, setChecking] = useState(false);
  const [q, setQ] = useState("");
  const [sugg, setSugg] = useState([]);
  const [open, setOpen] = useState(false);
  const zip = (v.zip || "").trim();
  const country = (v.country || "IT").toUpperCase();

  useEffect(() => {
    if (country !== "IT" || !/^\d{5}$/.test(zip)) { setGeo(null); return; }
    let active = true; setChecking(true);
    api.get("/geo/postal", { params: { country, zip } })
      .then((r) => { if (active) setGeo(r.data); })
      .catch(() => { if (active) setGeo(null); })
      .finally(() => { if (active) setChecking(false); });
    return () => { active = false; };
    // eslint-disable-next-line
  }, [zip, country]);

  // Auto-fill city from CAP when empty
  useEffect(() => {
    if (geo && geo.found && geo.places[0] && !(v.city || "").trim()) {
      on({ ...v, city: geo.places[0].city });
    }
    // eslint-disable-next-line
  }, [geo]);

  // Combo search (CAP or city) with debounce
  useEffect(() => {
    if (country !== "IT" || q.trim().length < 2) { setSugg([]); return; }
    const t = setTimeout(() => {
      api.get("/geo/search", { params: { q: q.trim(), country: "IT" } })
        .then((r) => { setSugg(r.data.results || []); setOpen(true); })
        .catch(() => setSugg([]));
    }, 250);
    return () => clearTimeout(t);
  }, [q, country]);

  const pick = (s) => { on({ ...v, city: s.city, zip: s.cap, country: "IT" }); setQ(""); setSugg([]); setOpen(false); };

  const cityMatch = (() => {
    if (country !== "IT") return true;
    if (!zip) return true;
    if (!/^\d{5}$/.test(zip)) return false;
    if (!geo || !geo.found) return true;
    const c = norm(v.city);
    if (!c) return true;
    return geo.places.some((p) => norm(p.city) === c);
  })();

  useEffect(() => { onCapStatus(cityMatch); /* eslint-disable-next-line */ }, [cityMatch]);

  const places = geo?.places?.map((p) => p.city).join(", ");
  return (
    <div className="rounded-xl border border-border p-4 space-y-2" data-testid={testid}>
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-semibold">{title}</p>
        {book.length > 0 && (
          <Select onValueChange={onLoad}>
            <SelectTrigger className="w-auto h-8 gap-1 text-xs" data-testid={`${testid}-book`}><BookUser className="w-3.5 h-3.5" /><SelectValue placeholder="Rubrica" /></SelectTrigger>
            <SelectContent>{book.map((b) => <SelectItem key={b.id} value={b.id} data-testid={`${testid}-book-${b.id}`}>{contactTitle(b)}{b.city ? ` — ${b.city}` : ""}</SelectItem>)}</SelectContent>
          </Select>
        )}
      </div>
      {country === "IT" && (
        <div className="relative">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input className="pl-9" placeholder="Cerca CAP o città (aggancio automatico)" value={q}
              onChange={(e) => setQ(e.target.value)} onFocus={() => sugg.length && setOpen(true)}
              onBlur={() => setTimeout(() => setOpen(false), 150)} data-testid={`${testid}-geo-search`} />
          </div>
          {open && sugg.length > 0 && (
            <div className="absolute z-20 mt-1 w-full rounded-lg border border-border bg-popover shadow-lg max-h-60 overflow-auto" data-testid={`${testid}-geo-results`}>
              {sugg.map((s, i) => (
                <button key={i} type="button" onMouseDown={() => pick(s)} className="w-full text-left px-3 py-2 text-sm hover:bg-secondary flex items-center justify-between gap-2" data-testid={`${testid}-geo-opt-${i}`}>
                  <span>{s.city} <span className="text-muted-foreground">({s.province})</span></span>
                  <span className="font-mono text-xs text-muted-foreground">{s.cap}{s.multi ? "…" : ""}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-2">
        <Input placeholder="Nome / Ragione sociale *" value={v.name || ""} onChange={(e) => on({ ...v, name: e.target.value })} data-testid={`${testid}-name`} />
        <Input type="tel" placeholder="Telefono *" value={v.phone || ""} onChange={(e) => on({ ...v, phone: e.target.value })} data-testid={`${testid}-phone`} />
        <Input className="sm:col-span-2" placeholder="Indirizzo e civico *" value={v.address || ""} onChange={(e) => on({ ...v, address: e.target.value })} data-testid={`${testid}-address`} />
        <Input placeholder="CAP *" value={v.zip || ""} onChange={(e) => on({ ...v, zip: e.target.value.replace(/[^0-9A-Za-z]/g, "") })} data-testid={`${testid}-zip`} />
        <Input placeholder="Città *" value={v.city || ""} onChange={(e) => on({ ...v, city: e.target.value })} data-testid={`${testid}-city`} />
        <div className="sm:col-span-1"><CountrySelect value={v.country} onChange={(c) => on({ ...v, country: c })} testid={`${testid}-country`} /></div>
        <Input placeholder="Email (opzionale)" value={v.email || ""} onChange={(e) => on({ ...v, email: e.target.value })} />
      </div>
      {country === "IT" && (
        <div className="text-xs min-h-[16px] flex items-center gap-1" data-testid={`${testid}-cap-status`}>
          {checking ? <span className="text-muted-foreground flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Verifico il CAP…</span>
            : !zip ? null
            : !/^\d{5}$/.test(zip) ? <span className="text-amber-500 flex items-center gap-1"><EyeOff className="w-3.5 h-3.5" /> Inserisci un CAP valido (5 cifre).</span>
            : geo && geo.found
              ? (cityMatch
                  ? <span className="text-teal-500 flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> Corrisponde: {places}{geo.places[0]?.state ? ` (${geo.places[0].state})` : ""}</span>
                  : <span className="text-amber-500 flex items-center gap-1"><EyeOff className="w-3.5 h-3.5" /> Il CAP {zip} risulta a <strong>{places}</strong>. Correggi la città.</span>)
              : <span className="text-teal-600/80">CAP non in elenco: scrivi la città e prosegui pure.</span>}
        </div>
      )}
      <label className="flex items-center gap-2 text-xs text-muted-foreground pt-1 cursor-pointer">
        <Checkbox checked={save} onCheckedChange={onSave} data-testid={`${testid}-save`} /> Salva in rubrica
      </label>
    </div>
  );
};

const AddrRecap = ({ who, a, testid }) => (
  <div className="rounded-xl border border-border p-4 text-sm" data-testid={testid}>
    <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">{who}</p>
    <p className="font-medium">{a.name || "—"}{a.phone && <> · <PhoneDisplay value={a.phone} data-testid={`${testid}-phone`} /></>}</p>
    <p className="text-muted-foreground">{[a.address, [a.zip, a.city].filter(Boolean).join(" "), a.country].filter(Boolean).join(", ")}</p>
  </div>
);

export default function ShipBooking() {
  const { user } = useAuth();
  return ["dealer", "admin", "customer", "agent"].includes(user?.role) ? <DealerShippingWizard key={user.id} /> : <StandardShipBooking />;
}

function StandardShipBooking() {
  const { user } = useAuth();
  const isDealer = user?.role === "dealer";
  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState("");
  const [ranges, setRanges] = useState([]);
  const [weightKey, setWeightKey] = useState("1");
  const [carriers, setCarriers] = useState([]);
  const [carrier, setCarrier] = useState(null);
  const [from, setFrom] = useState(emptyAddr());
  const [to, setTo] = useState(emptyAddr());
  const [book, setBook] = useState([]);
  const [saveFrom, setSaveFrom] = useState(false);
  const [saveTo, setSaveTo] = useState(false);
  const [contactEmail, setContactEmail] = useState("");
  const [content, setContent] = useState("");
  const [declaredValue, setDeclaredValue] = useState("");
  const [notes, setNotes] = useState("");
  const [insurance, setInsurance] = useState(false);
  const [terms, setTerms] = useState(false);
  const [addrConfirmed, setAddrConfirmed] = useState(false);
  const [fromCapOk, setFromCapOk] = useState(true);
  const [toCapOk, setToCapOk] = useState(true);
  const [showFix, setShowFix] = useState(false);
  const [result, setResult] = useState(null);
  const [locker, setLocker] = useState(null);
  const [dropMode, setDropMode] = useState("shop");
  const [loading, setLoading] = useState(false);
  const [ratesNotice, setRatesNotice] = useState(null);
  const bookingAttempt = useRef(null);

  const [shopSender, setShopSender] = useState(null);
  const loadBook = () => api.get("/addressbook").then((r) => setBook(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/shipping/options").then((r) => setRanges(r.data.weight_ranges || [])).catch(() => {});
    if (isDealer) api.get("/shipping/shop-sender").then((r) => setShopSender(r.data)).catch(() => {});
    loadBook();
  }, [isDealer]);

  const shopUsed = direction === "from_shop" || direction === "pickup_to_shop";
  const shopBlock = isDealer && shopUsed && shopSender && !shopSender.complete;
  const fromManual = direction !== "from_shop";      // mittente inserito a mano
  const toManual = direction !== "pickup_to_shop";   // destinatario inserito a mano
  const shopCountry = (shopSender?.address?.country || "IT").toUpperCase();
  const fromCountry = fromManual ? (from.country || "IT") : shopCountry;
  const toCountry = toManual ? (to.country || "IT") : shopCountry;

  // --- Validazione passo 1: dati minimi + CAP coerente prima dei corrieri ---
  const missFrom = fromManual ? missingOf(from) : [];
  const missTo = toManual ? missingOf(to) : [];
  const step1Problems = [];
  if (!direction) step1Problems.push("Seleziona la modalità di spedizione");
  if (shopBlock) step1Problems.push("Completa prima i dati del tuo negozio (vetrina)");
  missFrom.forEach((k) => step1Problems.push(`Mittente — ${FIELD_LABEL[k]} mancante`));
  missTo.forEach((k) => step1Problems.push(`Destinatario — ${FIELD_LABEL[k]} mancante`));
  if (fromManual && !fromCapOk) step1Problems.push("Mittente — il CAP non corrisponde alla città");
  if (toManual && !toCapOk) step1Problems.push("Destinatario — il CAP non corrisponde alla città");
  const step1Valid = step1Problems.length === 0;

  const loadRates = async () => {
    if (!step1Valid) { setShowFix(true); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/shipping/rates", { weight_key: weightKey, from_country: fromCountry, to_country: toCountry });
      setCarriers((data.carriers || []).filter((item) => item.cost_source === "vediloo" && item.vediloo_id));
      setRatesNotice(data.notice);
      setStep(2);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore tariffe"); }
    finally { setLoading(false); }
  };

  const chooseCarrier = (c) => { setCarrier(c); setStep(3); };

  const fillFrom = (setter) => (id) => {
    const b = book.find((x) => x.id === id);
    if (b) setter({ name: b.name || "", address: b.address || "", city: b.city || "", zip: b.zip || "", country: b.country || "IT", phone: b.phone || "", email: b.email || "" });
  };

  const doBook = async (payment) => {
    setLoading(true);
    try {
      const body = { direction, carrier: carrier?.id, weight_key: weightKey, payment, save_from: saveFrom, save_to: saveTo,
        content, declared_value: parseFloat(declaredValue) || 0, notes, insurance: insurance && ((parseFloat(declaredValue) || 0) > 0 && (parseFloat(declaredValue) || 0) <= 100), terms_accepted: terms,
        drop_mode: dropMode, locker_id: locker?.id, locker_name: locker?.name, locker_address: locker ? `${locker.address}, ${locker.city_line}` : undefined };
      if (carrier?.type === "locker" && !locker) { toast.error("Scegli il locker di destinazione sulla mappa"); setLoading(false); return; }
      if (payment === "link" && contactEmail.trim()) body.contact_email = contactEmail.trim();
      if (direction !== "from_shop") body.from_addr = from;
      if (direction !== "pickup_to_shop") body.to_addr = to;
      const fingerprint = JSON.stringify(body);
      if (bookingAttempt.current?.fingerprint !== fingerprint) bookingAttempt.current = { fingerprint, key: window.crypto.randomUUID() };
      const { data } = await api.post("/shipping/book", body, { headers: { "Idempotency-Key": bookingAttempt.current.key } });
      setResult(data);
      if (data.booking_state === "unknown" || data.mock) toast.warning("Prenotazione non confermata: non ripetere la richiesta, contatta l'assistenza.");
      else if (data.charged) toast.success(`Spedizione ${data.carrier} registrata — addebitati ${euro(data.amount)}`);
      else if (data.payment_link) toast.success(data.payment_link.email_sent ? "Link creato e inviato via email" : "Link di pagamento creato");
      loadBook();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore prenotazione"); }
    finally { setLoading(false); }
  };

  const copyLink = (url) => { navigator.clipboard.writeText(url); toast.success("Link copiato"); };
  const rangeLabel = ranges.find((r) => r.key === weightKey)?.label || `${weightKey} kg`;
  const val = parseFloat(declaredValue);
  const insurable = val > 0 && val <= 100;
  const insCost = insurance && insurable ? Math.round(val * 0.02 * 100) / 100 : 0;
  const displayTotal = Math.round(((carrier?.total || 0) + insCost) * 100) / 100;

  // Passo 3: restano solo i controlli su contenuto/valore/condizioni (indirizzi già validati al passo 1).
  const problems = [...step1Problems];
  if (!content.trim()) problems.push("Contenuto del pacco mancante");
  if (!(val > 0)) problems.push("Valore dichiarato mancante");
  if (!terms) problems.push("Accetta le condizioni generali di spedizione");
  const canPay = problems.length === 0;

  return (
    <div className="space-y-6 max-w-5xl" data-testid="ship-booking-page">
      <ServiceHistoryButton service="spedizioni" />
      <div>
        <div className="flex items-center gap-2"><Truck className="w-4 h-4 text-primary" /><span className="text-xs font-medium tracking-wider uppercase text-muted-foreground">Spedizioni</span></div>
        <h1 className="font-head text-3xl font-bold tracking-tight mt-2">Prenota spedizione</h1>
      </div>

      <div className="flex items-center gap-2">
        {STEPS.map((s, i) => (
          <React.Fragment key={s.n}>
            <div className={`flex items-center gap-2 ${step >= s.n ? "text-foreground" : "text-muted-foreground"}`} data-testid={`ship-step-${s.n}`}>
              <span className={`w-6 h-6 rounded-full text-xs flex items-center justify-center ${step >= s.n ? "bg-primary text-primary-foreground" : "bg-secondary"}`}>{s.n}</span>
              <span className="text-sm font-medium hidden sm:inline">{s.t}</span>
            </div>
            {i < STEPS.length - 1 && <div className="flex-1 h-px bg-border" />}
          </React.Fragment>
        ))}
      </div>

      {result && (
        <Card className="p-5 glow-border" data-testid="ship-result-card">
          {result.booking_state === "unknown" || result.mock ? <div className="text-amber-500 space-y-2" data-testid="ship-unconfirmed-warning"><p className="font-semibold">{result.mock ? "MOCKED · Ritiro non prenotato" : "Esito corriere da verificare"}</p><p className="text-sm">{result.message || "Operazione simulata: nessuna lettera di vettura reale disponibile."} {result.funds_reserved && `Importo trattenuto in attesa di verifica: ${euro(result.amount)}.`}</p></div> : <div className={`flex items-center gap-2 ${result.payment_link_error ? "text-amber-500" : "text-teal-500"}`} data-testid="ship-confirmed-status"><Check className="w-5 h-5" /><span className="font-semibold">Spedizione {result.charged ? "pagata" : "in attesa di pagamento"}</span></div>}
          <Link to="/app/console-spedizioni" className="text-sm text-primary underline inline-block mt-3" data-testid="ship-open-console">Apri la console spedizioni</Link>
          {!result.mock && result.booking_state === "confirmed" && result.id && <div className="mt-3"><ShipmentPickupButton shipmentId={result.id} variant="booking" /></div>}
          {result.payment_link_error && <p className="text-sm text-amber-500 mt-2" data-testid="ship-payment-link-error">{result.payment_link_error}</p>}
          {!result.mock && result.booking_state === "confirmed" && <p className="text-sm mt-2" data-testid="ship-email-result">{result.confirmation_email_status === "sent" ? "Riepilogo inviato all'email del tuo account." : "Riepilogo email non inviato: puoi riprovare dalla console senza riprenotare."}</p>}
          <div className="text-sm mt-3 space-y-1">
            <p>Corriere: <strong>{result.carrier}</strong></p>
            {!result.mock && result.tracking && <p>Tracking: <span className="font-mono">{result.tracking}</span></p>}
            {result.price ? (
              <div className="mt-2 pt-2 border-t border-border space-y-0.5" data-testid="ship-result-breakdown">
                <div className="flex justify-between"><span className="text-muted-foreground">Imponibile</span><span>{euro(result.price.subtotal)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">IVA 22%</span><span>{euro(result.price.vat)}</span></div>
                {result.price.insurance > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Assicurazione (IVA incl.)</span><span>{euro(result.price.insurance)}</span></div>}
                <div className="flex justify-between font-semibold text-base pt-1 border-t border-border/60 mt-1"><span>Totale (IVA inclusa)</span><span data-testid="ship-result-total">{euro(result.amount)}</span></div>
              </div>
            ) : (
              <p>Totale <span className="text-muted-foreground">(IVA inclusa)</span>: <strong>{euro(result.amount)}</strong></p>
            )}
          </div>
          {result.payment_link && (
            <div className="mt-4 rounded-xl border border-border p-4 flex flex-col sm:flex-row gap-4 items-center" data-testid="ship-pay-link-box">
              {result.payment_link.qr_url && <img src={`${BACKEND_URL}${result.payment_link.qr_url}`} alt="QR pagamento" className="w-56 h-56 rounded-lg bg-white p-3 shrink-0" data-testid="ship-pay-qr" />}
              <div className="flex-1 w-full space-y-2">
                <p className="text-sm">Apri il pagamento con carta. Nessuna etichetta viene richiesta prima della conferma dell'incasso.</p>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" className="gap-1" onClick={() => window.open(result.payment_link.checkout_url, "_blank")} data-testid="ship-pay-open"><ExternalLink className="w-4 h-4" /> Apri link</Button>
                  <Button size="sm" variant="outline" className="gap-1" onClick={() => copyLink(result.payment_link.checkout_url)} data-testid="ship-pay-copy"><Copy className="w-4 h-4" /> Copia link</Button>
                </div>
                {result.payment_link.email_sent && <p className="text-xs text-teal-500">Link inviato via email a {result.payment_link.contact_email}</p>}
                <p className="text-xs text-muted-foreground">Lo trovi anche nello storico "Pagamenti".</p>
              </div>
            </div>
          )}
        </Card>
      )}

      {step === 1 && (
        <Card className="p-6 glow-border space-y-4" data-testid="ship-step1">
          <div>
            <Label>Tipo di spedizione</Label>
            <Select value={direction} onValueChange={setDirection}>
              <SelectTrigger className="mt-1.5" data-testid="ship-direction-select"><SelectValue placeholder="Seleziona una modalità di spedizione" /></SelectTrigger>
              <SelectContent>
                {isDealer && <SelectItem value="from_shop">Parti dal mio negozio → cliente</SelectItem>}
                {isDealer && <SelectItem value="pickup_to_shop">Ritira dal cliente → al mio negozio</SelectItem>}
                <SelectItem value="custom">Personalizzato (ritiro e consegna a mia scelta)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {shopBlock && (
            <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 text-sm" data-testid="ship-shop-incomplete">
              <p className="font-medium text-amber-500">Completa prima i dati del tuo negozio</p>
              <p className="text-muted-foreground mt-1">L'indirizzo del tuo negozio viene usato come {direction === "from_shop" ? "mittente" : "destinatario"} ma non è completo (mancano: {(shopSender.missing || []).join(", ")}).</p>
              <Link to="/app/vetrina" className="text-primary underline mt-1 inline-block" data-testid="ship-shop-link">Vai a "La mia vetrina" →</Link>
            </div>
          )}
          <div>
            <Label>Peso del pacco</Label>
            <Select value={weightKey} onValueChange={setWeightKey}>
              <SelectTrigger className="mt-1.5" data-testid="ship-weight-range"><SelectValue placeholder="Seleziona fascia di peso" /></SelectTrigger>
              <SelectContent>{ranges.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}</SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-1">Colli fino a 20 kg.</p>
          </div>

          {direction && (
            <div className="space-y-3" data-testid="ship-step1-addresses">
              <p className="text-sm text-muted-foreground">Inserisci i dati minimi di mittente e destinatario: verranno usati per compilare in automatico la spedizione.</p>
              {fromManual
                ? <AddrForm title="Mittente / Ritiro" v={from} on={setFrom} book={book} onLoad={fillFrom(setFrom)} save={saveFrom} onSave={setSaveFrom} testid="ship-from-addr" onCapStatus={setFromCapOk} />
                : <ShopSummary who="Mittente" s={shopSender} testid="ship-from-auto" />}
              {toManual
                ? <AddrForm title="Destinatario / Consegna" v={to} on={setTo} book={book} onLoad={fillFrom(setTo)} save={saveTo} onSave={setSaveTo} testid="ship-to-addr" onCapStatus={setToCapOk} />
                : <ShopSummary who="Destinatario" s={shopSender} testid="ship-to-auto" />}
            </div>
          )}

          <StickyActions testId="ship-sticky-step1">
            {!direction
              ? <p className="text-xs text-muted-foreground mr-auto">Seleziona prima una modalità di spedizione.</p>
              : !step1Valid && <p className="text-xs text-amber-500 mr-auto" data-testid="ship-step1-hint">Completa i dati obbligatori (CAP e città coerenti).</p>}
            <Button onClick={loadRates} disabled={loading || !step1Valid} data-testid="ship-see-carriers">
              {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Cerco corrieri…</> : "Vedi corrieri disponibili"}
            </Button>
          </StickyActions>
        </Card>
      )}

      {step === 2 && (
        <Card className="p-6 glow-border space-y-4" data-testid="ship-step2">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">Corrieri per {rangeLabel} · {fromCountry} → {toCountry}</p>
            <Button variant="ghost" size="sm" className="gap-1" onClick={() => setStep(1)}><ArrowLeft className="w-4 h-4" /> Indietro</Button>
          </div>
          {ratesNotice && <p className="text-sm text-amber-500" data-testid="ship-rates-unavailable">{ratesNotice}</p>}
          <div className="grid sm:grid-cols-2 gap-3">
            {carriers.map((c) => (
              <button key={c.id} onClick={() => chooseCarrier(c)} data-testid={`ship-carrier-${c.id}`}
                className="text-left rounded-xl border border-border p-4 hover:border-primary transition-colors">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-3 min-w-0">
                    <CarrierLogo c={c} />
                    <span className="font-head font-semibold truncate">{c.name}</span>
                  </div>
                  <span className="font-head font-bold text-lg shrink-0">{euro(c.total)}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">{c.carrier_name ? `${c.carrier_name} · ` : ""}{c.type_label ? `${c.type_label} · ` : ""}{c.eta ? `Consegna ${c.eta} · ` : ""}IVA inclusa</p>
                <AdminCost cost={c.cost} note="IVA esclusa" className="mt-1" testId={`ship-carrier-cost-${c.id}`} />
              </button>
            ))}
            {carriers.length === 0 && <p className="text-muted-foreground text-sm">Nessun corriere disponibile per questa tratta.</p>}
          </div>
        </Card>
      )}

      {step === 3 && !result && (
        <Card className="p-6 glow-border space-y-4" data-testid="ship-step3">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">Corriere: <strong className="text-foreground">{carrier?.name}</strong> · {euro(displayTotal)} <span className="text-xs">IVA inclusa</span>{insCost > 0 && <span className="text-xs"> (incl. assicurazione)</span>}</p>
            <Button variant="ghost" size="sm" className="gap-1" onClick={() => setStep(2)}><ArrowLeft className="w-4 h-4" /> Cambia corriere</Button>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            {fromManual
              ? <AddrRecap who="Mittente" a={from} testid="ship-from-recap" />
              : <ShopSummary who="Mittente" s={shopSender} testid="ship-from-auto" />}
            {toManual
              ? <AddrRecap who="Destinatario" a={to} testid="ship-to-recap" />
              : <ShopSummary who="Destinatario" s={shopSender} testid="ship-to-auto" />}
          </div>
          <Button variant="ghost" size="sm" className="gap-1 -mt-1" onClick={() => setStep(1)} data-testid="ship-edit-addresses"><ArrowLeft className="w-4 h-4" /> Modifica indirizzi</Button>

          {carrier?.type === "locker" && (
            <div className="rounded-xl border border-border p-4 space-y-2" data-testid="ship-locker-dest">
              <p className="font-medium text-sm">Locker di destinazione (dove il destinatario ritira)</p>
              <LockerPicker testid="ship-dest-locker" initialQuery={[to.address, to.zip, to.city].filter(Boolean).join(" ")} value={locker} onSelect={setLocker} />
            </div>
          )}

          <div className="rounded-xl border border-border p-4 space-y-3" data-testid="ship-drop-mode">
            <p className="font-medium text-sm">Chi consegna il pacco al corriere?</p>
            <div className="flex flex-wrap gap-2">
              <Button type="button" size="sm" variant={dropMode === "shop" ? "default" : "outline"} className="gap-2" onClick={() => setDropMode("shop")} data-testid="ship-drop-shop"><Store className="w-4 h-4" /> Lo tengo io e spedisco dal negozio</Button>
              <Button type="button" size="sm" variant={dropMode === "customer" ? "default" : "outline"} className="gap-2" onClick={() => setDropMode("customer")} data-testid="ship-drop-customer"><UserRound className="w-4 h-4" /> Do l'etichetta al cliente</Button>
            </div>
            {dropMode === "customer" && (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">Stampa l'etichetta e consegnala al cliente. {carrier?.carrier === "inpost" ? "Trova i locker/punti InPost più vicini e copia le indicazioni da mandargli:" : "Il cliente consegna il pacco all'ufficio postale o punto del corriere più vicino."}</p>
                {carrier?.carrier === "inpost" && <LockerPicker testid="ship-drop-locker" initialQuery={[from.address, from.zip, from.city].filter(Boolean).join(" ")} />}
              </div>
            )}
          </div>

          <label className="flex items-start gap-2 text-sm cursor-pointer rounded-lg border border-primary/30 bg-primary/5 p-3" data-testid="ship-addr-confirm-label">
            <Checkbox checked={addrConfirmed} onCheckedChange={(v) => setAddrConfirmed(!!v)} data-testid="ship-addr-confirm" className="mt-0.5" />
            <span>Confermo che i dati di <strong>mittente</strong> e <strong>destinatario</strong> qui sopra sono corretti e completi.</span>
          </label>

          <div className="rounded-xl border border-border p-4 space-y-3" data-testid="ship-parcel-details">
            <p className="text-sm font-semibold">Contenuto del pacco</p>
            <div className="grid sm:grid-cols-2 gap-2">
              <div><Label className="text-xs">Contenuto *</Label><Input className="mt-1" placeholder="Es. abbigliamento, ricambi…" value={content} onChange={(e) => setContent(e.target.value)} data-testid="ship-content" /></div>
              <div><Label className="text-xs">Valore dichiarato (€) *</Label><Input className="mt-1" type="number" step="0.01" placeholder="0,00" value={declaredValue} onChange={(e) => setDeclaredValue(e.target.value)} data-testid="ship-value" /></div>
            </div>
            <div><Label className="text-xs">Note per il corriere (opzionale)</Label><Input className="mt-1" placeholder="Es. citofono, referente, orario di consegna…" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="ship-notes" /></div>
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <Checkbox checked={insurance && insurable} disabled={!insurable} onCheckedChange={(v) => setInsurance(!!v)} data-testid="ship-insurance" className="mt-0.5" />
              <span>Assicura la spedizione sul valore dichiarato {insCost > 0 && <strong className="text-foreground">(+{euro(insCost)})</strong>}<br /><span className="text-xs text-muted-foreground">Opzione proposta dal venditore: premio pari al <strong>2% del valore dichiarato</strong>, IVA inclusa. Assicurabile fino a un valore massimo di <strong>€ 100,00</strong>; oltre tale importo la spedizione non è assicurabile.</span>{val > 100 && <span className="block text-xs text-amber-500 mt-0.5">Valore dichiarato superiore a € 100,00: assicurazione non disponibile.</span>}</span>
            </label>
            <p className="text-xs text-muted-foreground">Contenuto e valore sono dichiarati sotto la responsabilità del mittente.</p>
          </div>

          {isDealer && (
            <div>
              <Label>Email cliente <span className="text-muted-foreground">(opzionale, per inviare il link)</span></Label>
              <Input type="email" className="mt-1.5" data-testid="ship-contact-email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} placeholder="cliente@email.it" />
            </div>
          )}

          <label className="flex items-start gap-2 text-sm cursor-pointer" data-testid="ship-terms-label">
            <Checkbox checked={terms} onCheckedChange={(v) => setTerms(!!v)} data-testid="ship-terms" className="mt-0.5" />
            <span>Accetto le <Link to="/app/condizioni-spedizione" target="_blank" className="text-primary underline">condizioni generali di spedizione</Link> (etichetta non rimborsabile una volta emessa, salvo mancato ritiro del corriere; assicurazione facoltativa al 2% del valore, copertura massima € 100,00).</span>
          </label>

          <StickyActions testId="ship-sticky-book">
            {canPay ? (
              <>
                {isDealer && <Button onClick={() => doBook("wallet")} disabled={loading || !addrConfirmed} data-testid="ship-book-wallet">Prenota e paga col wallet</Button>}
                <Button variant={isDealer ? "outline" : "default"} onClick={() => doBook("link")} disabled={loading || !addrConfirmed} data-testid="ship-book-link">Paga con carta</Button>
              </>
            ) : (
              <Button onClick={() => setShowFix(true)} disabled={loading} data-testid="ship-save-continue">Salva e continua</Button>
            )}
          </StickyActions>
          {canPay
            ? (addrConfirmed
                ? <p className="text-xs text-teal-500" data-testid="ship-ready">Tutti i dati sono corretti: puoi procedere al pagamento.</p>
                : <p className="text-xs text-amber-500" data-testid="ship-ready">Conferma mittente e destinatario per procedere al pagamento.</p>)
            : <p className="text-xs text-muted-foreground">Completa e verifica i dati per abilitare il pagamento.</p>}
        </Card>
      )}

      <Dialog open={showFix} onOpenChange={setShowFix}>
        <DialogContent data-testid="ship-fix-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-500" /> Aggiorna i dati</DialogTitle></DialogHeader>
          <DialogDescription className="text-sm text-muted-foreground">Prima di procedere completa o correggi quanto segue:</DialogDescription>
          <ul className="list-disc pl-5 text-sm space-y-1" data-testid="ship-fix-list">
            {problems.map((p) => <li key={p}>{p}</li>)}
          </ul>
          <Button onClick={() => setShowFix(false)} className="w-full" data-testid="ship-fix-close">Ho capito, correggo</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
